import { useEffect } from 'react';
import { useDesignerStore } from '../stores';
import { useCanvasOperations } from './useCanvasOperations';
import { useHistory } from './useHistory';

export const useKeyboardShortcuts = () => {
  const { setTool } = useDesignerStore();
  const {
    deleteSelected,
    copySelected,
    paste,
    duplicateSelected,
    bringForward,
    sendBackward,
    addText,
  } = useCanvasOperations();
  const { undo, redo, canUndo, canRedo } = useHistory();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input or textarea
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      // Also ignore if user is editing text in canvas (check for contenteditable)
      if ((e.target as HTMLElement).isContentEditable) {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Tool shortcuts (no modifier)
      if (!modifier && !e.shiftKey && !e.altKey) {
        switch (e.key.toLowerCase()) {
          case 'v':
            e.preventDefault();
            setTool('select');
            break;
          case 't':
            e.preventDefault();
            addText();
            setTool('select');
            break;
          case 'escape':
            e.preventDefault();
            setTool('select');
            break;
        }
      }

      // Modifier shortcuts
      if (modifier) {
        switch (e.key.toLowerCase()) {
          case 'z':
            e.preventDefault();
            if (e.shiftKey) {
              if (canRedo) redo();
            } else {
              if (canUndo) undo();
            }
            break;
          case 'y':
            e.preventDefault();
            if (canRedo) redo();
            break;
          case 'c':
            e.preventDefault();
            copySelected();
            break;
          case 'v':
            e.preventDefault();
            paste();
            break;
          case 'd':
            e.preventDefault();
            duplicateSelected();
            break;
          case 'a':
            // Note: Select all - would need canvas context to implement properly
            break;
        }
      }

      // Layer shortcuts
      if (modifier && e.key === ']') {
        e.preventDefault();
        bringForward();
      }
      if (modifier && e.key === '[') {
        e.preventDefault();
        sendBackward();
      }

      // Delete shortcut
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Only prevent default if not in an input
        if (
          !(e.target instanceof HTMLInputElement) &&
          !(e.target instanceof HTMLTextAreaElement)
        ) {
          e.preventDefault();
          deleteSelected();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    setTool,
    addText,
    undo,
    redo,
    canUndo,
    canRedo,
    copySelected,
    paste,
    duplicateSelected,
    bringForward,
    sendBackward,
    deleteSelected,
  ]);
};

export default useKeyboardShortcuts;
